import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createDistrict, updateException, getAllDistricts, getAllRegions } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const DistrictComponent = ({ exception, onClose, onResolved }) => {
    const [districtName, setDistrictName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [districts, setDistricts] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [selectedRegionId, setSelectedRegionId] = useState(null);
    const [showRegionSelect, setShowRegionSelect] = useState(false);

    useEffect(() => {
        const extractDistrictName = (message) => {
            setDistrictName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractDistrictName(exception.description);
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const [districtsResponse, regionsResponse] = await Promise.all([
                    getAllDistricts(),
                    getAllRegions()
                ]);
                
                const districtsData = districtsResponse.data?.data || [];
                setDistricts(districtsData);
                
                const regionsData = regionsResponse.data || [];
                setRegions(regionsData);
            } catch (error) {
                console.error("Error fetching data", error);
                message.error('Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [exception]);

    const handleMarkAsNew = () => {
        setShowRegionSelect(true);
    };

    const handleRegionSelect = (value) => {
        console.log('setSelectedRegionId inside DistrictComponent.js', value);
        setSelectedRegionId(value);
    };

    const handleCreateDistrict = async () => {
        if (!districtName || !selectedRegionId) {
            message.error('Please provide both district name and region');
            return;
        }

        if (exception.status === 'pending') {
            try {
                setLoading(true);
                const districtData = {
                    name: districtName,
                    region_id: selectedRegionId,
                    alias_for: "0"
                };
                
                await createDistrict(districtData);
                await updateException(exception.id);
                console.log(`District "${districtName}" created successfully.`);
                message.success('District created successfully');
            } catch (error) {
                console.error(`Failed to create district "${districtName}".`, error);
                message.error('Failed to create district');
            } finally {
                setLoading(false);
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Exception already resolved.");
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleDistrictSelect = (value) => {
        const selectedDistrict = districts.find(district => district.id === value);
        setSelectedAliasId(selectedDistrict ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId?.id) return;
        if (exception.status === 'pending') {
            try {
                setLoading(true);
                const districtData = {
                    name: districtName,
                    region_id: selectedAliasId.region_id,
                    alias_for: selectedAliasId.id
                };
                
                await createDistrict(districtData);
                await updateException(exception.id);
                console.log(`District "${districtName}" set as alias for district with ID: ${selectedAliasId.id}`);
                message.success('Alias saved successfully');
            } catch (error) {
                console.error(`Failed to update alias for district with ID: ${selectedAliasId.id}`, error);
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Exception already resolved.");
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias
                        </Button>
                        
                        {showRegionSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666', width: '100%', marginBottom: 8 }}>
                                    {districtName ? `Select a region for the new district "${districtName}".` : 'Select a region for the new district.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a region"
                                    onChange={handleRegionSelect}
                                    disabled={loading}
                                    value={selectedRegionId ?? undefined}
                                >
                                    {regions?.map((region) => (
                                        <Option key={region.id} value={region.id}>
                                            {region.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleCreateDistrict}
                                    disabled={loading || !selectedRegionId}
                                    loading={loading}
                                >
                                    Create District
                                </Button>
                            </>
                        )}
                        
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666', width: '100%', marginBottom: 8 }}>
                                    {districtName ? `Select an existing district to alias "${districtName}" to.` : 'Select an existing district to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a district"
                                    onChange={handleDistrictSelect}
                                    disabled={loading}
                                    value={selectedAliasId?.id ?? undefined}
                                >
                                    {districts?.map((district) => (
                                        <Option key={district.id} value={district.id}>
                                            {district.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId?.id}
                                    loading={loading}
                                >
                                    Save Alias
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default DistrictComponent;
